import {
  AcademicCapIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  CheckCircleIcon,
  ClockIcon,
  MinusCircleIcon,
  MinusIcon,
  NoSymbolIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { clsx } from "clsx";
import React from "react";
import { CgBorderStyleDashed } from "react-icons/cg";
import { FaHourglassEnd } from "react-icons/fa";

import { convertReadableString } from "@components/forms/utils";

type StatusStyle = {
  icon: React.ElementType;
  bgClass: string;
  textClass: string;
  labelOverride?: string;
};

const STATUS_STYLES: Record<string, StatusStyle> = {
  // Frontera / Passage
  BASELINE: { icon: CgBorderStyleDashed, bgClass: "bg-[#FDF4FF]", textClass: "text-[#A900B7]" },
  TREATMENT: { icon: PlayCircleIcon, bgClass: "bg-[#E5FBFF]", textClass: "text-[#057C9A]" },
  MASTERED: { icon: AcademicCapIcon, bgClass: "bg-[#F0FDF4]", textClass: "text-[#15803D]" },
  MAINTENANCE: { icon: ArrowPathIcon, bgClass: "bg-[#FAF5FF]", textClass: "text-[#8200DA]" },
  GENERALIZATION: { icon: ArrowsPointingOutIcon, bgClass: "bg-[#FAF5FF]", textClass: "text-[#8200DA]" },
  HOLD: { icon: PauseCircleIcon, bgClass: "bg-red-50", textClass: "text-red-600" },
  CLOSED: { icon: XCircleIcon, bgClass: "bg-limestone-100", textClass: "text-limestone-600" },
  INACTIVE: { icon: FaHourglassEnd, bgClass: "bg-[#C1B9B0]", textClass: "text-[#4E4440]" },

  // Central Reach (extra statuses needed to account for central reach's unique statuses and status naming without spaces)
  PROGRESS: { icon: PlayCircleIcon, bgClass: "bg-[#E5FBFF]", textClass: "text-[#057C9A]" },
  NOT_ACTIVE: { icon: MinusCircleIcon, bgClass: "bg-[#C1B9B0]", textClass: "text-[#4E4440]" },
  NOTACTIVE: {
    icon: MinusCircleIcon,
    bgClass: "bg-[#C1B9B0]",
    textClass: "text-[#4E4440]",
    labelOverride: "Not Active",
  },
  MET: { icon: CheckCircleIcon, bgClass: "bg-[#F0FDF4]", textClass: "text-[#15803D]" },
  DISCONTINUED: { icon: NoSymbolIcon, bgClass: "bg-limestone-100", textClass: "text-limestone-600" },
  ON_HOLD: { icon: PauseCircleIcon, bgClass: "bg-red-50", textClass: "text-red-600" },
  "ON HOLD": { icon: PauseCircleIcon, bgClass: "bg-red-50", textClass: "text-red-600", labelOverride: "On Hold" },

  // HiRasmus
  ACTIVE: { icon: PlayCircleIcon, bgClass: "bg-[#E5FBFF]", textClass: "text-[#057C9A]" },
  WAITING: { icon: ClockIcon, bgClass: "bg-limestone-100", textClass: "text-limestone-600" },

  // HippHealth / Motivity
  IN_PROGRESS: { icon: PlayCircleIcon, bgClass: "bg-[#E5FBFF]", textClass: "text-[#057C9A]" },
  IN_MAINTENANCE: { icon: ArrowPathIcon, bgClass: "bg-[#FAF5FF]", textClass: "text-[#8200DA]" },
  ARCHIVED: { icon: ArchiveBoxIcon, bgClass: "bg-limestone-100", textClass: "text-limestone-600" },
  FUTURE: { icon: ClockIcon, bgClass: "bg-blue-50", textClass: "text-blue-600" },
  NOT_INTRODUCED: { icon: MinusIcon, bgClass: "bg-limestone-100", textClass: "text-limestone-600" },
  COMPLETED: { icon: CheckCircleIcon, bgClass: "bg-[#F0FDF4]", textClass: "text-[#15803D]" },
  DEACTIVATED: { icon: MinusCircleIcon, bgClass: "bg-[#C1B9B0]", textClass: "text-[#4E4440]" },
  BASELINE_ASSESS: { icon: CgBorderStyleDashed, bgClass: "bg-[#FDF4FF]", textClass: "text-[#A900B7]" },

  // Rethink
  NOT_STARTED: { icon: MinusCircleIcon, bgClass: "bg-limestone-100", textClass: "text-limestone-600" },
  STARTED: { icon: PlayCircleIcon, bgClass: "bg-[#E5FBFF]", textClass: "text-[#057C9A]" },
  REINTRODUCED: { icon: ArrowPathIcon, bgClass: "bg-[#FAF5FF]", textClass: "text-[#8200DA]" },
};

const DEFAULT_STYLE: StatusStyle = {
  icon: MinusIcon,
  bgClass: "bg-limestone-100",
  textClass: "text-limestone-600",
};

interface GoalStatusLabelProps {
  status: string | null | undefined;
  className?: string;
}

export const GoalStatusLabel: React.FC<GoalStatusLabelProps> = ({ status, className }) => {
  if (!status) return null;

  const normalized = status.toUpperCase();
  const { icon: Icon, bgClass, textClass, labelOverride } = STATUS_STYLES[normalized] ?? DEFAULT_STYLE;

  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium whitespace-nowrap",
        bgClass,
        textClass,
        className,
      )}
    >
      <Icon className="w-3 h-3" />
      {labelOverride ?? convertReadableString(normalized)}
    </span>
  );
};
